import { ChunkListMO } from '../../models/ChunkListMO';
import { ChunkDataMO } from '../../models/ChunkDataMO';
import { wordData } from '../../database/wordData';
import { WordTypeCountMO } from '../../models/WordTypeCountMO';
import { WordTypeScoreMO } from '../../models/WordTypeScoreMO';
import { ConfigManager } from '../../Manager/ConfigManager';
import { WordManager } from '../../Manager/WordManager';
import { ParagraphMO } from '../../models/ParagraphMO';
import { WordMO } from '../../models/WordMO';
import { ExclusionsMO } from '../../models/ExclusionsMO';

export enum Metrics {
	Overall,
	Verbs,
	Nouns,
	Prepositions,
	AdjectivesAdverbs,
	WasteWords,
}
const scoreContributionWeight = [1, 2, 4, 16, 32];
const scoreAverageWeightCutoff = [8, 12, 24, 64];
const strippedOutExclusionPunctuationsRex =
	ConfigManager.getStrippedOutExclusionPunctuations();

export class Analysis {
	private previousLength: number;

	constructor() {
		this.previousLength = -1;
	}

	private createChunkDataMO(paragraphMOs: ParagraphMO[]): ChunkDataMO {
		let title = '';
		for (let i = 0; i < paragraphMOs.length; i++) {
			if (paragraphMOs[i].wc > 0) {
				for (
					let k = 0;
					k < Math.min(paragraphMOs[i].words.length, 6) && title.length < 35;
					k++
				) {
					title += paragraphMOs[i].words[k].word + ' ';
				}
				title = title.trim();
				break;
			}
		}

		return new ChunkDataMO(title, paragraphMOs);
	}

	isTextChange(text: string) {
		if (text.length !== this.previousLength) {
			this.previousLength = text.length;
			return true;
		}
		return false;
	}

	// according to spec, proper nouns that are capitalised after full stops are treated
	isFirstLetterCapitalize(word: string, followsPeriod: boolean) {
		return !followsPeriod && word.charAt(0) !== word.charAt(0).toLowerCase();
	}
	// verbs
	private identifVerbs(word: string, followsPeriod: boolean) {
		let result: boolean = false;
		const wordLowercase = word.toLowerCase();

		for (let i = 0; i < wordData.verbTokenRegex.length; i++) {
			if (wordLowercase.endsWith(wordData.verbTokenRegex[i]) === true) {
				result = true;
				break;
			}
		}

		if (result === false) {
			for (let i = 0; i < wordData.verbTokenMatch.length; i++) {
				if (wordData.verbTokenMatch[i] === wordLowercase) {
					result = true;
					break;
				}
			}
		}
		if (result && this.isFirstLetterCapitalize(word, followsPeriod)) {
			return false;
		}

		return result;
	}

	// nouns
	private identifyNouns(word: string, followsPeriod: boolean) {
		let result: boolean = false;
		const wordLowercase = word.toLowerCase();

		for (let i = 0; i < wordData.nounTokenRegex.length; i++) {
			if (wordLowercase.endsWith(wordData.nounTokenRegex[i]) === true) {
				result = true;
				break;
			}
		}

		if (!result) {
			return false;
		} else {
			if (wordData.nounExceptionMatch.find((x) => x === wordLowercase)) {
				return false;
			}
			if (this.isFirstLetterCapitalize(word, followsPeriod)) {
				return false;
			}
		}

		return result;
	}

	// prepositions
	private identifyPrepositions(word: string, followsPeriod: boolean) {
		let result: boolean = false;
		const wordLowercase = word.toLowerCase();

		for (let i = 0; i < wordData.prepositionTokenMatch.length; i++) {
			if (wordData.prepositionTokenMatch[i] === wordLowercase) {
				result = true;
				break;
			}
		}
		if (result && this.isFirstLetterCapitalize(word, followsPeriod)) {
			return false;
		}

		return result;
	}

	// ad_
	private identifyAd_s(word: string, followsPeriod: boolean) {
		let result: boolean = false;
		const wordLowercase = word.toLowerCase();

		for (let i = 0; i < wordData.adjTokenRegex.length; i++) {
			if (wordLowercase.endsWith(wordData.adjTokenRegex[i]) === true) {
				result = true;
				break;
			}
		}

		if (result) {
			for (let i = 0; i < wordData.adjectiveAdverbExceptionMatch.length; i++) {
				if (wordData.adjectiveAdverbExceptionMatch[i] === wordLowercase) {
					return false;
				}
			}
			if (this.isFirstLetterCapitalize(word, followsPeriod)) {
				return false;
			}
		}

		return result;
	}

	// waste
	private identifyWastes(word: string, followsPeriod: boolean) {
		let result: boolean = false;
		const wordLowercase = word.toLowerCase();

		for (let i = 0; i < wordData.wasteWordTokenMatch.length; i++) {
			if (wordData.wasteWordTokenMatch[i] === wordLowercase) {
				result = true;
				break;
			}
		}
		if (result && this.isFirstLetterCapitalize(word, followsPeriod)) {
			return false;
		}

		return result;
	}

	public identifyWord(word: string, followsPeriod: boolean) {
		if (this.identifVerbs(word, followsPeriod) === true) {
			return 0;
		} else if (this.identifyNouns(word, followsPeriod) === true) {
			return 1;
		} else if (this.identifyPrepositions(word, followsPeriod) === true) {
			return 2;
		} else if (this.identifyAd_s(word, followsPeriod) === true) {
			return 3;
		} else if (this.identifyWastes(word, followsPeriod) === true) {
			return 4;
		}
		return -1;
	}

	private excludeSingleQuote(words: WordMO[]) {
		let startSingleQuote: boolean = false;
		for (let i = 0; i < words.length; i++) {
			const wordMO = words[i];
			if (
				wordMO.word.startsWith('‘') ||
				wordMO.word.startsWith("'") ||
				startSingleQuote
			) {
				wordMO.exclude = true;
				startSingleQuote = true;
			}

			if (wordMO.word.endsWith('’') || wordMO.word.endsWith("'")) {
				wordMO.exclude = true;
				startSingleQuote = false;
			}
		}
	}

	private excludeDoubleQuote(words: WordMO[]) {
		let startDoubleQuote: boolean = false;
		for (let i = 0; i < words.length; i++) {
			const wordMO = words[i];
			if (
				wordMO.word.startsWith('“') ||
				wordMO.word.startsWith('"') ||
				startDoubleQuote
			) {
				wordMO.exclude = true;
				startDoubleQuote = true;
			}

			if (wordMO.word.endsWith('”') || wordMO.word.endsWith('"')) {
				wordMO.exclude = true;
				startDoubleQuote = false;
			}
		}
	}

	private excludeParens(words: WordMO[]) {
		let startParens: boolean = false;

		for (let i = 0; i < words.length; i++) {
			const wordMO = words[i];
			if (wordMO.word.includes('(') || startParens) {
				wordMO.exclude = true;
				startParens = true;
			}

			if (wordMO.word.includes(')')) {
				wordMO.exclude = true;
				startParens = false;
			}
		}
	}

	private calculateWord(
		paragraph: ParagraphMO,
		exclusionsMO: ExclusionsMO,
		remainingWordCount: number,
	) {
		let _verb = 0;
		let _waste = 0;
		let _noun = 0;
		let _prep = 0;
		let _ad_ = 0;
		let wc = 0;
		if (
			(exclusionsMO.headings && paragraph.isHeading) ||
			(exclusionsMO.tables && paragraph.isTable) ||
			(exclusionsMO.indentedParagraphs && paragraph.isLeftIndent) ||
			(exclusionsMO.listItems && paragraph.isList) ||
			exclusionsMO.wordCountLessThan > paragraph.wc
		) {
			for (let k = 0; k < paragraph.words.length; k++) {
				paragraph.words[k].exclude = true;
			}
		} else {
			if (exclusionsMO.parens) {
				this.excludeParens(paragraph.words);
			}
			if (exclusionsMO.singleQuotes) {
				this.excludeSingleQuote(paragraph.words);
			}
			if (exclusionsMO.doubleQuotes) {
				this.excludeDoubleQuote(paragraph.words);
			}
			let followsPeriod = true;
			for (let k = 0; k < paragraph.words.length; k++) {
				const wordMO: WordMO = paragraph.words[k];
				const lowerCaseWord = wordMO.word.toLowerCase();
				const strippedWord = lowerCaseWord.replace(
					strippedOutExclusionPunctuationsRex,
					'',
				);
				if (exclusionsMO.lowerCaseCustomExclusion.indexOf(strippedWord) >= 0) {
					wordMO.exclude = true;
				}

				// ignore excluding word
				if (!wordMO.exclude) {
					wc++;

					// eslint-disable-next-line
					WordManager.separateWord(wordMO.word).forEach((term) => {
						switch (this.identifyWord(term, followsPeriod)) {
							case 0:
								_verb++;
								break;
							case 1:
								_noun++;
								break;
							case 2:
								_prep++;
								break;
							case 3:
								_ad_++;
								break;
							case 4:
								_waste++;
								break;

							default:
								break;
						}
					});
				}
				followsPeriod =
					lowerCaseWord.endsWith('.') && !lowerCaseWord.endsWith('..');
				if (wc === remainingWordCount) {
					paragraph.words = paragraph.words.slice(0, k + 1);
					break;
				}
			}
		}
		if (exclusionsMO.wordCountLessThan > wc && wc < remainingWordCount) {
			paragraph.wordTypeCount = new WordTypeCountMO();
			paragraph.wc = 0;
		} else {
			paragraph.wordTypeCount = new WordTypeCountMO(
				_verb,
				_noun,
				_prep,
				_waste,
				_ad_,
			);
			paragraph.wc = wc;
		}
		return paragraph;
	}

	private calculateScore(
		count: number,
		wordCount: number,
		scoreCutoffs: number[],
	) {
		// get to fixed 2 decimals to make consistent values shown in dianogsis
		const rating = Math.round((count * 100) / wordCount) || 0;
		let score = 0;
		while (score < scoreCutoffs.length && rating >= scoreCutoffs[score]) {
			score++;
		}

		const scoreContribution = scoreContributionWeight[score];
		return {
			scoreContribution,
			score,
			// show fully column upto the given rating
			graphScore: count === 0 ? 0 : score + 1,
			rating,
		};
	}

	private calculateChunkNodeScores(
		wordTypeCount: WordTypeCountMO,
		totalWord: number,
	) {
		let metrics = ConfigManager.getMetrics();
		// verbs
		var verbScore = this.calculateScore(
			wordTypeCount.verb,
			totalWord,
			metrics.Verbs.scoreCutoffs,
		);

		// nouns
		var nounScore = this.calculateScore(
			wordTypeCount.noun,
			totalWord,
			metrics.Nouns.scoreCutoffs,
		);

		// prep
		var prepScore = this.calculateScore(
			wordTypeCount.prep,
			totalWord,
			metrics.Prepositions.scoreCutoffs,
		);

		// ad_word
		var ad_Score = this.calculateScore(
			wordTypeCount.ad_,
			totalWord,
			metrics.AdjectivesAdverbs.scoreCutoffs,
		);

		// waste
		var wasteScore = this.calculateScore(
			wordTypeCount.waste,
			totalWord,
			metrics.WasteWords.scoreCutoffs,
		);

		// average
		let totalWeight =
			verbScore.scoreContribution +
			nounScore.scoreContribution +
			prepScore.scoreContribution +
			ad_Score.scoreContribution +
			wasteScore.scoreContribution;

		let averageScore = 0;
		while (
			averageScore < scoreAverageWeightCutoff.length &&
			totalWeight >= scoreAverageWeightCutoff[averageScore]
		) {
			averageScore++;
		}
		return new WordTypeScoreMO(
			verbScore.score,
			nounScore.score,
			prepScore.score,
			wasteScore.score,
			ad_Score.score,
			averageScore,
			verbScore.rating,
			nounScore.rating,
			prepScore.rating,
			wasteScore.rating,
			ad_Score.rating,
			verbScore.graphScore,
			nounScore.graphScore,
			prepScore.graphScore,
			wasteScore.graphScore,
			ad_Score.graphScore,
		);
	}

	private createParagraphMO(paragrapgh: string) {
		let result = WordManager.getWords(paragrapgh);

		return new ParagraphMO(
			paragrapgh,
			result.words,
			false,
			false,
			false,
			false,
			result.wc,
		);
	}

	public countEligibleWords(
		chunkListMO: ChunkListMO,
		paragraphs: string[],
		exclusionsMO: ExclusionsMO,
		nodePerProcess: number,
	) {
		let chunkListMOLengthBeforeProcess = chunkListMO.lastLoadedParagraphIndex;
		let isNodesProcess = false;
		const maxWordCount = 1000;
		do {
			var currenParagraph = paragraphs[chunkListMO.lastLoadedParagraphIndex++];
			if (currenParagraph.trim().length === 0) continue;
			const paragraphMO = this.createParagraphMO(currenParagraph);
			this.calculateWord(
				paragraphMO,
				exclusionsMO,
				maxWordCount - chunkListMO.contentWordCount,
			);
			chunkListMO.contentWordCount += paragraphMO.wc;
			if (chunkListMO.contentWordCount === maxWordCount) {
				chunkListMO.lastLoadedParagraphIndex = paragraphs.length;
			}
			chunkListMO.paragraphMOs.push(paragraphMO);
			chunkListMO.wordTypeCount.append(paragraphMO.wordTypeCount);
			isNodesProcess =
				chunkListMO.lastLoadedParagraphIndex -
					chunkListMOLengthBeforeProcess ===
				nodePerProcess;
		} while (
			!isNodesProcess &&
			paragraphs.length > chunkListMO.lastLoadedParagraphIndex
		);
		chunkListMO.wordTypeScore = this.calculateChunkNodeScores(
			chunkListMO.wordTypeCount,
			chunkListMO.contentWordCount,
		);
		return chunkListMO;
	}
	public splitIntoChunks(chunkListMOInput: ChunkListMO) {
		// have to recreate because input has no function even it's ChunkListMO
		let chunkListMO: ChunkListMO = new ChunkListMO();
		chunkListMO.clone(chunkListMOInput);
		if (chunkListMO.isFewWordCount) {
			chunkListMO.wordTypeCount = new WordTypeCountMO();
			chunkListMO.wordTypeScore = new WordTypeScoreMO();
		} else {
			let newChunk = this.createChunkDataMO(chunkListMO.paragraphMOs);
			newChunk.wordTypeCount = chunkListMO.wordTypeCount;
			newChunk.contentWordCount = chunkListMO.contentWordCount;
			newChunk.wordTypeScore = chunkListMO.wordTypeScore;
			chunkListMO.addLast(newChunk);
		}
		return chunkListMO;
	}
}
